/**
 * The monthly AI allowance, as the screens use it (ticket 08).
 *
 * Every beta learner has a share of one shared monthly budget. Anything that
 * calls a model — analysing a new moment, building or refreshing a pack, an
 * exam, a "listen" that is not cached yet, a pronunciation check — is paid
 * for out of it; studying, practising and reviewing what is already there
 * cost nothing and never stop working. These helpers keep the wording and the
 * rounding identical everywhere: cents on screen, full precision on the
 * server.
 */
import { resetDay } from './drafts-ui';
import { currentLang, num, t } from './i18n';
import { confirmed, currentOwner } from './session';
import type { AiAllowance, Allowance } from './types';

/** Money as the learner reads it. */
export function usd(amount: number): string {
  const value = (Math.round(amount * 100) / 100).toFixed(2);
  return `$${currentLang() === 'fa' ? num(value) : value}`;
}

/** The AI block of an allowance reply; undefined in personal mode. */
export function aiOf(a?: Allowance): AiAllowance | undefined {
  return a && a.limit !== null ? a.ai : undefined;
}

/** True once nothing further can be paid for this month. */
export function aiOut(a?: Allowance): boolean {
  return aiOf(a)?.exhausted === true;
}

/** The running line: "AI: $0.14 of $0.20 left this month". */
export function aiLine(ai: AiAllowance): string {
  return t('ai.line', { left: usd(ai.remainingUsd), total: usd(ai.allocationUsd) });
}

/**
 * Why a paid control is unavailable and when it returns. The two limits read
 * differently on purpose: one is the learner's own share, the other is the
 * whole beta's pot, and only the owner can do anything about the second.
 */
export function aiReason(ai: AiAllowance): string {
  return t(ai.reason === 'beta-budget' ? 'ai.reasonBeta' : 'ai.reasonOwn', { date: resetDay(ai.resetsAt) });
}

interface AllocationSnapshot {
  allocationUsd: number;
  /** The allocation value that changed, retained so the explanation stays visible across screens/reloads. */
  changedFromUsd?: number;
}

/** A visible change, in cents, to this account's allocation in the same accounting month. */
export function allocationChangeFrom(
  previous: Pick<AiAllowance, 'month' | 'allocationUsd'>,
  current: Pick<AiAllowance, 'month' | 'allocationUsd'>,
): { before: number; after: number } | undefined {
  if (previous.month !== current.month || Math.round(previous.allocationUsd * 100) === Math.round(current.allocationUsd * 100)) return undefined;
  return { before: previous.allocationUsd, after: current.allocationUsd };
}

/**
 * Remember the last observed allocation per learner and accounting month.
 * A changed share can move the remaining amount without an AI request, so
 * keep that explanation alongside the current monthly line.
 */
export function observeAllocationChange(ai?: AiAllowance): string | undefined {
  const owner = currentOwner();
  if (!confirmed() || !owner || !ai || typeof localStorage === 'undefined') return undefined;
  const key = `li-ai-allocation:${owner}:${ai.month}`;
  try {
    const raw = localStorage.getItem(key);
    const stored = raw ? JSON.parse(raw) as AllocationSnapshot : undefined;
    const previous = stored && Number.isFinite(stored.allocationUsd) && stored.allocationUsd >= 0 ? stored : undefined;
    // A stored change marker from an older or tampered write is dropped, like a
    // bad allocation is: the persisted explanation must be a real amount.
    const changedStored = previous && previous.changedFromUsd !== undefined &&
      Number.isFinite(previous.changedFromUsd) && previous.changedFromUsd >= 0
      ? previous.changedFromUsd : undefined;
    const change = previous ? allocationChangeFrom({ month: ai.month, allocationUsd: previous.allocationUsd }, ai) : undefined;
    const changedFromUsd = change ? previous?.allocationUsd : changedStored;
    const next: AllocationSnapshot = { allocationUsd: ai.allocationUsd, ...(changedFromUsd !== undefined ? { changedFromUsd } : {}) };
    localStorage.setItem(key, JSON.stringify(next));
    return changedFromUsd === undefined
      ? undefined
      : t('ai.allocationChanged', { before: usd(changedFromUsd), after: usd(ai.allocationUsd) });
  } catch {
    // Storage can be disabled in private browsing. The live allowance remains authoritative.
    return undefined;
  }
}
